import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { usePageMeta } from '../hooks/usePageMeta';
import { BookOpenIcon, AwardIcon, MonitorIcon, ClipboardCheckIcon, BriefcaseIcon, LaptopIcon, CheckCircle2Icon, ChevronDownIcon, ChevronLeftIcon, LoaderIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
import { umaPrograms, UMA_TCPA_DISCLOSURE } from '../data/umaPrograms';
import { TCPADisclosure } from '../components/TCPADisclosure';
const umaEducationLevels = [{
  label: 'High School Diploma',
  value: '1'
}, {
  label: 'GED',
  value: '3'
}, {
  label: '0-12 College Credits',
  value: '4'
}, {
  label: 'More Than 12 College Credits',
  value: '5'
}, {
  label: 'Associates Degree',
  value: '6'
}, {
  label: 'Bachelors Degree',
  value: '7'
}, {
  label: 'Graduate Degree',
  value: '8'
}];
const umaStartTimeframes = [{
  label: 'Immediately',
  value: '1'
}, {
  label: '1-3 Months',
  value: '2'
}, {
  label: '4-6 Months',
  value: '3'
}, {
  label: '7-12 Months',
  value: '4'
}, {
  label: 'Later Than 1 Year',
  value: '5'
}];
function getGradYearOptions(): string[] {
  const years: string[] = [];
  for (let y = 2026; y >= 1980; y--) {
    years.push(String(y));
  }
  return years;
}
const gradYearOptions = getGradYearOptions();
/**
 * Resolves a program from the URL so a campaign link can skip the picker step.
 * Accepts ?program= (slug or route slug) or ?mProgram= (webhook code).
 */
function getProgramSlugFromUrl(): string {
  const params = new URLSearchParams(window.location.search);
  const requested = (params.get('program') || params.get('mProgram') || '').trim();
  if (!requested) return '';
  const match = umaPrograms.find(p => p.slug.toLowerCase() === requested.toLowerCase() || p.routeSlug.toLowerCase() === requested.toLowerCase() || p.mProgram.toLowerCase() === requested.toLowerCase());
  return match?.slug || '';
}
export function UMALandingPage() {
  usePageMeta('Ultimate Medical Academy Online | Healthcare Career Training Programs', 'Explore online healthcare degree programs at Ultimate Medical Academy. Get personalized support, financial aid options, and job search assistance. Request free info today.', {
    ogImage: 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  const navigate = useNavigate();
  const formRef = useRef<HTMLDivElement>(null);
  const [preselectedProgram] = useState(getProgramSlugFromUrl);
  const [selectedProgram, setSelectedProgram] = useState(preselectedProgram);
  const [formStep, setFormStep] = useState(preselectedProgram ? 1 : 0);
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [userIP, setUserIP] = useState('');
  const [urlParams] = useState<Record<string, string>>(() => {
    const params: Record<string, string> = {};
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  });
  const {
    leadIdToken,
    trustedFormUrl
  } = useLeadTracking();
  // Fetch user IP on mount
  useEffect(() => {
    fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => setUserIP(data.ip || '')).catch(() => setUserIP(''));
  }, []);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
          setZipLookedUp(true);
        }
      } else {
        setZipLookedUp(true);
      }
    } catch {
      setZipLookedUp(true);
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const handleProgramChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSlug = e.target.value;
    setSelectedProgram(newSlug);
    if (newSlug && formStep === 0) {
      setFormStep(1);
    }
  };
  const handleStep1Next = () => {
    if (validateUniStep1()) setFormStep(2);
  };
  const handleStep2Next = () => {
    const zipValid = validateUniStep2();
    const addrValid = formData.address.trim().length > 0;
    if (!addrValid) {
      setAddressError('Street address is required.');
    } else {
      setAddressError('');
    }
    if (zipValid && addrValid) {
      lookupZip(formData.zipCode);
      setFormStep(3);
    }
  };
  const handleStep3Submit = async () => {
    if (validateUniStep3()) {
      setIsSubmitting(true);
      const currentProgram = umaPrograms.find(p => p.slug === selectedProgram);
      try {
        const params = new URLSearchParams({
          ...urlParams,
          mProgram: currentProgram?.mProgram || '',
          highestEducation: formData.currentEducation,
          gradYear: formData.gradYear,
          startTimeframe: formData.startDate,
          zipCode: formData.zipCode,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: stripPhone(formData.phone),
          leadIdToken: leadIdToken,
          trustedFormUrl: trustedFormUrl,
          webLandingURL: window.location.href,
          userIP: userIP
        });
        await fetch(`https://hooks.zapier.com/hooks/catch/6973915/u7uetpg/?${params.toString()}`, {
          method: 'GET',
          mode: 'no-cors'
        });
      } catch (err) {
        console.error('Webhook submission error:', err);
      } finally {
        setIsSubmitting(false);
      }
      navigate('/thank-you', {
        state: {
          formData,
          partner: {
            name: 'Ultimate Medical Academy',
            logo: 'https://cdn.magicpatterns.com/uploads/ftg2brgTZFks7CVCNZ5jT1/UMA_LOGO_(1).webp',
            primaryColor: '#003B5C',
            accentColor: '#00BFA5',
            backUrl: '/colleges/ultimate-medical-academy',
            programTitle: currentProgram?.title || ''
          }
        }
      });
    }
  };
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const totalSteps = 3;
  const progressPercent = formStep === 0 ? 0 : Math.round(formStep / totalSteps * 100);
  const selectClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#4ECDC4] focus:border-[#4ECDC4]'}`;
  const inputClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#4ECDC4] focus:border-[#4ECDC4]'}`;
  const currentProgramTitle = umaPrograms.find(p => p.slug === selectedProgram)?.title;
  const renderFormContent = () => {
    if (formStep === 0) {
      return <div className="space-y-4" data-id="element-2873" data-mp="3rerk9z1ty5">
          <div data-id="element-2874" data-mp="3rau2u2uijk45">
            <label htmlFor="hero-program-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2875" data-mp="3rac32w8n6nrh">
              Select a Program
            </label>
            <div className="relative" data-id="element-2876" data-mp="1wwsyx3k3w6x7">
              <select id="hero-program-select" value={selectedProgram} onChange={handleProgramChange} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#4ECDC4] focus:border-[#4ECDC4] font-medium text-gray-800" data-id="element-2877" data-mp="1wy4b9rrmamzx">
                <option value="" data-id="element-2878" data-mp="1kuhwhl7rssde">-- Choose a Program --</option>
                {umaPrograms.map(p => <option key={p.slug} value={p.slug} data-id="element-2879" data-mp="1pst55smbwwu1">
                    {p.title}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-2880" data-mp="s6ca6u12ivbg">
                <ChevronDownIcon className="h-4 w-4" data-id="element-2881" data-mp="x5yo75oq0flb" />
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 text-center" data-id="element-2882" data-mp="125bkeh1hez0k">
            Select a program above to get started
          </p>
        </div>;
    }
    if (formStep === 1) {
      return <div className="space-y-4" data-id="element-2883" data-mp="2dhm2ntilncqr">
          <div data-id="element-2884" data-mp="2ih72051oln40">
            <label htmlFor="step1-program" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2885" data-mp="2igpa1km1xv1w">
              Program
            </label>
            <div className="relative" data-id="element-2886" data-mp="6vog8pj0and8">
              <select id="step1-program" value={selectedProgram} onChange={handleProgramChange} className="block w-full px-4 py-3 text-sm border border-gray-200 rounded-md bg-gray-50 appearance-none cursor-pointer font-medium text-gray-700" data-id="element-2887" data-mp="1uilkm5scq12">
                {umaPrograms.map(p => <option key={p.slug} value={p.slug} data-id="element-2888" data-mp="3o8iqlqj6ep54">
                    {p.title}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400" data-id="element-2889" data-mp="3f9f8opvqwfs7">
                <ChevronDownIcon className="h-4 w-4" data-id="element-2890" data-mp="3abdep9ws6w1y" />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3" data-id="element-2891" data-mp="35csmnxroniol" />

          <div data-id="element-2892" data-mp="18iz30sktyntz">
            <label htmlFor="startDate" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2893" data-mp="18jh5w82000av">
              When Would You Like to Start?
            </label>
            <select id="startDate" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClasses(!!errors.startDate)} data-id="element-2894" data-mp="1tf70fa5yztx3">
              <option value="" data-id="element-2895" data-mp="1oizrr9m6w4bg">-- Please Select --</option>
              {umaStartTimeframes.map(opt => <option key={opt.value} value={opt.value} data-id="element-2896" data-mp="22eajie5509bl">
                  {opt.label}
                </option>)}
            </select>
            {errors.startDate && <p className="mt-1 text-xs text-red-600" data-id="element-2897" data-mp="1aqa656akp1ev">{errors.startDate}</p>}
          </div>

          <div data-id="element-2898" data-mp="15qx8dqi5y9xi">
            <label htmlFor="currentEducation" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2899" data-mp="15rfctaezbugi">
              Highest Level of Education
            </label>
            <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses(!!errors.currentEducation)} data-id="element-2900" data-mp="1eprcs31qk4lm">
              <option value="" data-id="element-2901" data-mp="n0fqyi2s70rg">-- Please Select --</option>
              {umaEducationLevels.map(level => <option key={level.value} value={level.value} data-id="element-2902" data-mp="cvfvwfr1d9db">
                  {level.label}
                </option>)}
            </select>
            {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-2903" data-mp="2xjnvwteaujf">
                {errors.currentEducation}
              </p>}
          </div>

          <div data-id="element-2904" data-mp="7vtkmburq375">
            <label htmlFor="gradYear" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2905" data-mp="7xees0d67k4h">
              High School Graduation Year
            </label>
            <select id="gradYear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClasses(!!errors.gradYear)} data-id="element-2906" data-mp="10ljzin48594b">
              <option value="" data-id="element-2907" data-mp="qn47mhxdhvtr">-- Please Select --</option>
              {gradYearOptions.map(yr => <option key={yr} value={yr} data-id="element-2908" data-mp="qp76lrgi6zam">
                  {yr}
                </option>)}
            </select>
            {errors.gradYear && <p className="mt-1 text-xs text-red-600" data-id="element-2909" data-mp="3bduwqx79q034">{errors.gradYear}</p>}
          </div>

          <div className="flex gap-3 pt-2" data-id="element-2910" data-mp="1hucuhtrtvfrx">
            {!preselectedProgram && <button type="button" onClick={() => setFormStep(0)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-2911" data-mp="1hwp7dsiqazy4">
                <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-2912" data-mp="13687idlqsi3c" />
                Back
              </button>}
            <button type="button" onClick={handleStep1Next} className="flex-1 bg-gradient-to-r from-[#00BFA5] to-[#4ECDC4] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-2913" data-mp="y752uu9ocdvw">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 2) {
      return <div className="space-y-4" data-id="element-2914" data-mp="k8ynovci92lk">
          <div data-id="element-2915" data-mp="2jjq3e5roop84">
            <label htmlFor="zipCode" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2916" data-mp="2jk869l8uq1p0">
              Zip Code
            </label>
            <input type="text" id="zipCode" value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) lookupZip(val);
          }} className={inputClasses(!!errors.zipCode)} placeholder="12345" maxLength={5} data-id="element-2917" data-mp="2ej2837wf9m83" />
            {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-2918" data-mp="30v4zoubl4f5i">{errors.zipCode}</p>}
            {zipLoading && <div className="mt-2 flex items-center text-xs text-gray-500" data-id="element-2919" data-mp="237mvlwsvc17u">
                <LoaderIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-2920" data-mp="28552zir8m8dq" />
                Looking up your location...
              </div>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-2921" data-mp="2i3uachlicafq">
                <div data-id="element-2922" data-mp="3oivbd1pe3it6">
                  <label htmlFor="city" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2923" data-mp="3ojehzm9lakrw">
                    City
                  </label>
                  <input type="text" id="city" value={formData.city} onChange={e => updateFormData('city', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#4ECDC4] focus:border-[#4ECDC4] bg-gray-50" placeholder="City" data-id="element-2924" data-mp="3tjsogzvjlmnt" />
                </div>
                <div data-id="element-2925" data-mp="3w2e2jv8seicu">
                  <label htmlFor="state" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2926" data-mp="3w24jkrm5tbgn">
                    State
                  </label>
                  <input type="text" id="state" value={formData.state} onChange={e => updateFormData('state', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#4ECDC4] focus:border-[#4ECDC4] bg-gray-50" placeholder="State" data-id="element-2927" data-mp="9v5yqxdimjix" />
                </div>
              </div>
              <div data-id="element-2928" data-mp="xjyw8y9sczfn">
                <label htmlFor="address" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2929" data-mp="xla1z3mskh9e">
                  Street Address
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => {
              updateFormData('address', e.target.value);
              if (addressError) setAddressError('');
            }} className={`block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${addressError ? 'border-red-400' : 'border-gray-300 focus:ring-[#4ECDC4] focus:border-[#4ECDC4]'}`} placeholder="123 Main St" data-id="element-2930" data-mp="1c8ijem6p1itt" />
                {addressError && <p className="mt-1 text-xs text-red-600" data-id="element-2931" data-mp="si6tf5xraff1">{addressError}</p>}
              </div>
            </>}

          <div className="flex gap-3 pt-2" data-id="element-2932" data-mp="xcb1jsircthp">
            <button type="button" onClick={() => setFormStep(1)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-2933" data-mp="jjl0qhyklk0j">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-2934" data-mp="9l5kja12zv5t" />
              Back
            </button>
            <button type="button" onClick={handleStep2Next} className="flex-1 bg-gradient-to-r from-[#00BFA5] to-[#4ECDC4] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-2935" data-mp="ej74dx6bzyic">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 3) {
      return <div className="space-y-4" data-id="element-2936" data-mp="3cw3mhckks9yy">
          <div className="grid grid-cols-2 gap-3" data-id="element-2937" data-mp="3cwwnry0rm79p">
            <div data-id="element-2938" data-mp="edo9xxuzdutb">
              <label htmlFor="firstName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2939" data-mp="ee7t1hwdap8x">
                First Name
              </label>
              <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses(!!errors.firstName)} placeholder="Jane" data-id="element-2940" data-mp="4fs0rbllt9xu" />
              {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-2941" data-mp="2p6is7xiz1qad">{errors.firstName}</p>}
            </div>
            <div data-id="element-2942" data-mp="2k6ekox01k5wa">
              <label htmlFor="lastName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2943" data-mp="2k5v018j0b3ek">
                Last Name
              </label>
              <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses(!!errors.lastName)} placeholder="Doe" data-id="element-2944" data-mp="14np03dpt0m6s" />
              {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-2945" data-mp="1ee9sukisqwhs">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-2946" data-mp="1jbt9fr2yvlcw">
            <div data-id="element-2947" data-mp="1jf7bszbmrukc">
              <label htmlFor="email" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2948" data-mp="1ne6e0c5z9d83">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses(!!errors.email)} placeholder="jane@example.com" data-id="element-2949" data-mp="1sbgaucvp4r5y" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-2950" data-mp="2yl9020uadl7">{errors.email}</p>}
            </div>
            <div data-id="element-2951" data-mp="30fupijxq9qc">
              <label htmlFor="phone" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2952" data-mp="24vvfobp8yr88">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} className={inputClasses(!!errors.phone)} placeholder="(555)123-4567" maxLength={13} data-id="element-2953" data-mp="1zuzei5wa5j6i" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-2954" data-mp="1lwr8z9l6h9jq">{errors.phone}</p>}
            </div>
          </div>

          <TCPADisclosure checked={formData.tcpaConsent} onChange={checked => updateFormData('tcpaConsent', checked)} error={errors.tcpaConsent} checkboxClassName="mt-0.5 h-5 w-5 rounded border-gray-300 text-[#00BFA5] focus:ring-[#00BFA5] cursor-pointer flex-shrink-0" data-id="element-2955" data-mp="1gypul1yp5yel">
            {UMA_TCPA_DISCLOSURE}
          </TCPADisclosure>

          <div className="flex gap-3 pt-2" data-id="element-2956" data-mp="3792fmmf0utkz">
            <button type="button" onClick={() => setFormStep(2)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-2957" data-mp="32bj07j6l8arg">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-2958" data-mp="3ev68o4ypw92e" />
              Back
            </button>
            <button type="button" onClick={handleStep3Submit} disabled={isSubmitting} className="flex-1 bg-gradient-to-r from-[#00BFA5] to-[#4ECDC4] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-2959" data-mp="30vf2urictyzh">
              {isSubmitting && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-2960" data-mp="3jtrbmc5ct6uj" />}
              {isSubmitting ? 'Submitting...' : 'Request Info'}
            </button>
          </div>
        </div>;
    }
    return null;
  };
  return <div className="min-h-screen bg-white font-sans text-gray-800" data-id="element-2961" data-mp="fehul7j2jb2s">
      {/* A. Custom Header */}
      <header className="bg-white py-2 sm:py-4 px-4 sm:px-12 flex items-center justify-center shadow-sm" data-id="element-2962" data-mp="k69gv5v6yun4">
        <img src="https://cdn.magicpatterns.com/uploads/ftg2brgTZFks7CVCNZ5jT1/UMA_LOGO_(1).webp" alt="Ultimate Medical Academy Logo" className="h-10 sm:h-16 w-auto object-contain" data-id="element-2963" data-mp="2bqkh9cvvdkn0" />
      </header>

      {/* B. Hero Section */}
      <section className="bg-gradient-to-r from-[#176B9C] to-[#003B5C] relative overflow-hidden" data-id="element-2964" data-mp="220ho9cor06oo">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none bg-no-repeat bg-cover bg-center mix-blend-soft-light opacity-70" style={{
        backgroundImage: "url('https://cdn.magicpatterns.com/uploads/8uFwSQxxc9xYA37moQruEX/ChatGPT_Image_May_6,_2026,_08_20_39_PM.png')"
      }} data-id="element-2965" data-mp="26yj9o49nci3b" />
        {/* Explicit grid placement rather than duplicated copy: on mobile the
            form follows the headline so it lands within reach, and the
            supporting paragraph moves below it. */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-24 grid gap-8 lg:grid-cols-2 lg:gap-x-16 lg:gap-y-6 relative z-10" data-id="element-2966" data-mp="3dgzakjymssxl">
          {/* Headline */}
          <div className="text-white max-w-xl mx-auto lg:mx-0 text-center lg:text-left lg:col-start-1 lg:row-start-1 lg:self-end" data-id="element-2967" data-mp="16syzaz9mgggx">
            <p className="text-[#4ECDC4] font-medium mb-3 text-lg tracking-wide" data-id="element-2968" data-mp="11unmqgrxzrv0">
              Ultimate Medical Academy Online<sup className="text-xs" data-id="element-2969" data-mp="1gjjx4khh1gys">1</sup>
            </p>
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.05] text-white tracking-tight" data-id="element-2970" data-mp="1bq8nycp6so3p">
              {umaPrograms.find(p => p.slug === selectedProgram)?.shortTitle || <>
                  Healthcare
                  <br data-id="element-2971" data-mp="1lj680n9tul4f" />
                  Career Training
                </>}
            </h1>
          </div>

          {/* Right Form */}
          <div ref={formRef} className="w-full lg:w-[520px] lg:justify-self-end bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.3)] p-6 sm:p-10 relative z-10 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:self-center" data-id="element-2972" data-mp="1uh00fn4h0umb">
            <form onSubmit={e => e.preventDefault()} id="uma-lead-form" data-id="element-2973" data-mp="4sbqriqt9yz">
              {/* Hidden inputs for tracking */}
              <input type="hidden" name="xxTrustedFormCertUrl" id="xxTrustedFormCertUrl" value="" data-id="element-2974" data-mp="207t952p4g3x4" />
              <input type="hidden" name="leadid_token" id="leadid_token" value="" data-id="element-2975" data-mp="256xgrowedtlc" />

              <h2 className="text-2xl sm:text-3xl font-bold text-[#003B5C] mb-4" data-id="element-2976" data-mp="294v9h1pdmxth">
                Learn how we can help you succeed.
              </h2>

              {/* Progress Bar */}
              <div className="w-full bg-gray-100 rounded-full h-2 mb-8 overflow-hidden" data-id="element-2977" data-mp="2j3t50ol7zrlz">
                <div className="bg-gradient-to-r from-[#00BFA5] to-[#4ECDC4] h-2 rounded-full transition-all duration-500 ease-out" style={{
                width: `${progressPercent}%`
              }} data-id="element-2978" data-mp="2o2nmnn7gba4i"></div>
              </div>

              <AnimatePresence mode="wait" data-id="element-2979" data-mp="2wv7loornngpv">
                <motion.div key={formStep} initial={{
                opacity: 0,
                x: 10
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-2980" data-mp="31vlx8gsgt8sn">
                  {renderFormContent()}
                </motion.div>
              </AnimatePresence>
            </form>
          </div>

          {/* Supporting copy. After the form in the markup so mobile gets it
              below; the grid puts it back under the headline on desktop. */}
          <p className="text-base lg:text-lg text-white/85 max-w-lg mx-auto lg:mx-0 text-center lg:text-left leading-relaxed lg:col-start-1 lg:row-start-2 lg:self-start" data-id="element-2981" data-mp="3hw3pn4bhjnk">
            UMA students get personalized attention throughout the entire
            education journey. Our students get support from the very beginning,
            throughout career training, and after graduation.
          </p>
        </div>
      </section>

      {/* C. Benefits Icons Row */}
      <section className="bg-[#F4F9FB] py-12 border-b border-gray-200" data-id="element-2982" data-mp="hj8gxqs3f75r">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2983" data-mp="hh5kop0ckhsl">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 text-center" data-id="element-2984" data-mp="2076fieadgk0">
            {[{
            icon: BookOpenIcon,
            title: 'One-on-one\nAcademic Support'
          }, {
            icon: AwardIcon,
            title: 'Certification Prep²',
            sup: '2'
          }, {
            icon: MonitorIcon,
            title: '24/7 Technical\nSupport'
          }, {
            icon: ClipboardCheckIcon,
            title: "Apply Degree Credits\nToward a Bachelor's³",
            sup: '3'
          }, {
            icon: BriefcaseIcon,
            title: 'Job Search\nAssistance'
          }, {
            icon: LaptopIcon,
            title: 'Laptops for those\nwho qualify⁴',
            sup: '4'
          }].map((item, idx) => <div key={idx} className="flex flex-col items-center" data-id="element-2985" data-mp="13psuxu1ng9f3" data-mpi={idx}>
                <item.icon className="w-10 h-10 text-[#176B9C] mb-3 stroke-[1.5]" data-id="element-2986" data-mp="13ohrxwg135p1" />
                <p className="text-xs font-bold text-[#176B9C] whitespace-pre-line leading-tight" data-id="element-2987" data-mp="18ic8ws4916kf">
                  {item.title.replace(/²|³|⁴/g, '')}
                  {item.sup && <sup className="text-[10px]" data-id="element-2988" data-mp="2o7bu792wtq6c">{item.sup}</sup>}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* D. Three Feature Cards */}
      <section className="py-16 bg-white" data-id="element-2989" data-mp="2xxmzp028s7fc">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2990" data-mp="2xyyb9m1xijh0">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-2991" data-mp="2a9n7dvpa88yn">
            {[{
            img: 'https://cdn.magicpatterns.com/uploads/dFP2Bkreu2BvfkWxRv6Va3/Image_1.webp',
            title: 'Financial Aid',
            desc: 'Let us help you explore options for financial aid. We will help you through the process to see if you qualify.'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/9FrwRXe1BYLuSp6GRYweVA/image_2.webp',
            title: 'Job Search Assistance',
            desc: 'Our Career Service team helps graduates pursue job opportunities, schedule interviews, and more.'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/qcGrjYfSLHXUMihbDJMyJz/image_3.webp',
            title: 'Engaging Online Learning',
            desc: 'Get individual instructor feedback, personalized advisor support, and interact with peers on discussion posts.'
          }].map((card, idx) => <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col" data-id="element-2992" data-mp="gheutycpdo4b" data-mpi={idx}>
                <img src={card.img} alt={card.title} className="w-full h-48 object-cover" data-id="element-2993" data-mp="3exprrxv1dftu" />
                <div className="p-6 flex-1" data-id="element-2994" data-mp="3jwj2jiixusqb">
                  <h3 className="text-xl font-bold text-[#003B5C] mb-3" data-id="element-2995" data-mp="3oxgkbu01dkra">
                    {card.title}
                  </h3>
                  <p className="text-sm text-[#176B9C] font-medium leading-relaxed" data-id="element-2996" data-mp="3tpzxiputadvk">
                    {card.desc}
                  </p>
                </div>
              </div>)}
          </div>
          <div className="text-center" data-id="element-2997" data-mp="316kyrem6blsh">
            <button onClick={scrollToForm} className="inline-block bg-gradient-to-r from-[#00BFA5] to-[#4ECDC4] text-white font-bold py-3 px-12 rounded-full hover:shadow-lg transition-all" data-id="element-2998" data-mp="1id1af71zr0wv">
              Take the Next Step
            </button>
          </div>
        </div>
      </section>

      {/* E. '4 out of 5' Stat Section */}
      <section className="bg-[#F4F9FB] py-16" data-id="element-2999" data-mp="14e1kzx6nmhzn">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-center gap-12" data-id="element-3000" data-mp="14fcr43nko1tx">
          <div className="text-center md:text-right max-w-xs" data-id="element-3001" data-mp="unp4cg1yn8ys">
            <div className="text-[#00BFA5] font-bold text-5xl sm:text-6xl mb-2" data-id="element-3002" data-mp="um5ogqk7uzxj">
              4 <span className="text-[#176B9C]" data-id="element-3003" data-mp="qmdb80jvo5lu">out of 5</span>
            </div>
            <p className="text-[#176B9C] text-sm font-medium" data-id="element-3004" data-mp="lntohxxgmpsf">
              surveyed employers say they are likely to hire a UMA graduate
              again.⁵
            </p>
          </div>
          <div className="flex gap-2 sm:gap-4" data-id="element-3005" data-mp="bob7nqcujk4l">
            {[1, 2, 3, 4].map(i => <div key={i} className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-4 border-[#D8E8F0] flex items-center justify-center bg-white" data-id="element-3006" data-mp="20is8b34cjsnv">
                <CheckCircle2Icon className="w-10 h-10 sm:w-12 sm:h-12 text-[#00BFA5]" data-id="element-3007" data-mp="24j2heonfqz6y" />
              </div>)}
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-4 border-[#D8E8F0] flex items-center justify-center bg-transparent" data-id="element-3008" data-mp="1qk2p970ps261">
              <CheckCircle2Icon className="w-10 h-10 sm:w-12 sm:h-12 text-[#D8E8F0]" data-id="element-3009" data-mp="1gv1bzoljzyvr" />
            </div>
          </div>
        </div>
      </section>

      {/* F. 25% Georgetown Study Section */}
      <section className="py-16 bg-white" data-id="element-3010" data-mp="17u37l577dpcn">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3011" data-mp="17ve90yd6el0l">
          <div className="bg-[#003B5C] rounded-2xl overflow-hidden relative flex flex-col md:flex-row items-center shadow-xl" data-id="element-3012" data-mp="1csxwmovrka57">
            <div className="p-10 md:p-16 flex-1 z-10 flex items-center gap-8" data-id="element-3013" data-mp="xwyhiua8l9qk">
              <div className="text-6xl sm:text-8xl font-bold text-white" data-id="element-3014" data-mp="12vjgyrhnzpdy">
                25<span className="text-4xl sm:text-6xl" data-id="element-3015" data-mp="j988isemai4k">%</span>
              </div>
              <div className="w-1 h-24 bg-[#00BFA5] hidden sm:block" data-id="element-3016" data-mp="jbay5bbuy98r"></div>
              <p className="text-white text-lg sm:text-xl font-medium max-w-sm leading-snug" data-id="element-3017" data-mp="f97j6cpzcmiq">
                A Georgetown study found that an associate degree can increase
                lifetime earnings over a high school diploma by 25 percent.⁶
              </p>
            </div>
            <div className="w-full md:w-1/2 h-64 md:h-full absolute right-0 top-0 opacity-80 md:opacity-100" data-id="element-3018" data-mp="126rji1kmyatm">
              <img src="https://cdn.magicpatterns.com/uploads/xs9nMQM6BrsxQ4Td97ACQ9/image.png" alt="Graduate smiling" className="w-full h-full object-cover object-right" data-id="element-3019" data-mp="y5z8p3w47xv8" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#003B5C] to-transparent" data-id="element-3020" data-mp="16yj0plhwj473"></div>
            </div>
          </div>
        </div>
      </section>

      {/* G. Testimonial Section */}
      <section className="bg-[#176B9C] py-16 relative" data-id="element-3021" data-mp="1gxrsq8t7i8qt">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-3022" data-mp="1gv5l696bft8l">
          <div className="text-[#00BFA5] text-6xl font-serif leading-none mb-4" data-id="element-3023" data-mp="2iavvwhd4obd3">
            "
          </div>
          <p className="text-white text-xl sm:text-2xl font-medium leading-relaxed mb-8" data-id="element-3024" data-mp="2dahl4rk56nbd">
            "I'm a working mom and mother of four. I am thankful to UMA for the
            flexibility, so I am able to complete my courses and pass with
            honors!"
          </p>
          <div className="flex items-center justify-center gap-4" data-id="element-3025" data-mp="1zedv7agax14p">
            <div className="text-right" data-id="element-3026" data-mp="1ulugub9si1yu">
              <div className="text-white font-bold text-lg" data-id="element-3027" data-mp="1ukrpy3cgvzix">Merelesita R.</div>
              <div className="text-white/80 text-sm" data-id="element-3028" data-mp="1pnsxdp8gc0mu">UMA Graduate</div>
            </div>
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-white/20" data-id="element-3029" data-mp="1lmft3jl0c5i1">
              <img src="https://cdn.magicpatterns.com/uploads/xs9nMQM6BrsxQ4Td97ACQ9/image.png" alt="Merelesita R." className="w-full h-full object-cover" data-id="element-3030" data-mp="1gl9y1f3vk65c" />
            </div>
          </div>
        </div>
        {/* Triangle pointer */}
        <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[16px] border-l-transparent border-r-[16px] border-r-transparent border-t-[16px] border-t-[#176B9C]" data-id="element-3031" data-mp="3nabw21j9itjk"></div>
      </section>

      {/* H. Accreditation Bar */}
      <section className="bg-[#F8F9FA] py-12 mt-4" data-id="element-3032" data-mp="558s4k518nkq">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center gap-6" data-id="element-3033" data-mp="52uuc3iywx9h">
          <div className="w-20 h-20 flex-shrink-0 bg-white rounded-full p-2 shadow-sm flex items-center justify-center border border-gray-100" data-id="element-3034" data-mp="39j6v8w45rv7n">
            <AwardIcon className="w-10 h-10 text-[#003B5C]" data-id="element-3035" data-mp="3djpnusdvl5aw" />
          </div>
          <p className="text-sm text-gray-600 leading-relaxed" data-id="element-3036" data-mp="3ic0ra57tr987">
            UMA is institutionally accredited by the Accrediting Bureau of
            Health Education Schools (ABHES.org), a national accrediting agency
            that is recognized by the U.S. Secretary of Education. Ultimate
            Medical Academy does not recruit or enroll residents of the European
            Union.
          </p>
        </div>
      </section>

      {/* I. Citations Section */}
      <section className="bg-[#F8F9FA] py-8 border-t border-gray-200" data-id="element-3037" data-mp="3o56am4z6wafp">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3038" data-mp="6ppu98rs217p">
          <h4 className="text-xs font-bold text-gray-800 mb-3" data-id="element-3039" data-mp="1r69hbox6vgx">Citations</h4>
          <ol className="text-[10px] text-gray-500 space-y-1 list-decimal list-inside" data-id="element-3040" data-mp="3a6zkxc4e9gr4">
            <li data-id="element-3041" data-mp="3584wnunnqzzm">
              Coursework is online except for on-site externship/professional
              practice experience. Set schedules required for on-site
              externship/professional practice experience courses in select
              programs and coursework deadlines are set by instructors.
            </li>
            <li data-id="element-3042" data-mp="ygxc5y7d7oxt">
              Learners must meet eligibility criteria to sit for applicable
              certification exams
            </li>
            <li data-id="element-3043" data-mp="ui6d3ljc9x82">
              Tuition considerations and credit transfer decisions are at the
              discretion of the receiving institution.
            </li>
            <li data-id="element-3044" data-mp="16zh4dwdjbp70">
              For full information on laptop program eligibility, please see the
              UMA Catalog.
            </li>
            <li data-id="element-3045" data-mp="s3t3ag3iji7c">
              Source: Survey was conducted by Hanover Research. Employers that
              hired UMA Online students between Jul 1, 2022 - September 30,
              2023. Survey responses received from 257 employers. Excludes
              corporate alliance partners.
            </li>
            <li data-id="element-3046" data-mp="1b0asoquw360f">
              Georgetown University. The College Payoff. Study found that an
              associate degree can increase lifetime earnings over a high school
              diploma by 25 percent.
            </li>
          </ol>
          <p className="text-[10px] text-gray-500 mt-4" data-id="element-3047" data-mp="23nx60qtx5p0h">
            Where national data regarding earnings or employability within a
            particular field are cited, individual readers should take note that
            national averages may not accurately reflect the earnings of workers
            in their particular part of the country, and may include earners at
            all stages of their career, not just entry-level wages for recent
            graduates.
          </p>
          <p className="text-[10px] text-gray-500 mt-2" data-id="element-3048" data-mp="5uw47qswjpgq">
            Set schedules required for on-site externship / professional
            practice experience courses in select programs and in all programs,
            coursework deadlines are set by instructors.
          </p>
        </div>
      </section>

      {/* J. UMA Footer */}
      <footer className="bg-white py-12 border-t border-gray-200" data-id="element-3049" data-mp="tq8ldt084c62">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center md:items-start gap-8" data-id="element-3050" data-mp="orxb5h60nznb">
          <img src="https://cdn.magicpatterns.com/uploads/ftg2brgTZFks7CVCNZ5jT1/UMA_LOGO_(1).webp" alt="Ultimate Medical Academy Logo" className="h-14 object-contain" data-id="element-3051" data-mp="1m9niz0x4ky7k" />
          <div className="text-center md:text-left text-xs text-gray-600" data-id="element-3052" data-mp="1prbmhsrilivx">
            <p className="mb-1" data-id="element-3053" data-mp="1trbyl6r55hzt">
              © 2026 UMA Education, Inc. d/b/a Ultimate Medical Academy. All
              rights reserved.
            </p>
            <p className="mb-4" data-id="element-3054" data-mp="1ypnbjqcqg8m9">
              A member of the Vocate Education Solutions System
            </p>
            <div className="flex items-center justify-center md:justify-start gap-2 text-[#176B9C]" data-id="element-3055" data-mp="23nori2ev3rti">
              <a href="#" className="hover:underline" data-id="element-3056" data-mp="28nbeh3khsmfi">
                Marketing Statistics
              </a>
              <span data-id="element-3057" data-mp="2hhfzh6cy33n4">|</span>
              <a href="#" className="hover:underline" data-id="element-3058" data-mp="2hh6bfobgn6k2">
                Privacy Policy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}